import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getCountryCodeFromApplication } from 'components/Applications/utils/getCountryCodeFromApplication';
import AvailabilityStatus from 'components/AvailabilityStatus/AvailabilityStatus';
import EyeAttachmentPreview from 'components/EyeAttachmentPreview/EyeAttachmentPreview';
import LocationDisplay from 'components/LocationDisplay/LocationDisplay';
import ProfileMatch from 'components/ProfileMatch/ProfileMatch';
import SkillMatch from 'components/SkillMatch/SkillMatch';
import TruncatedText from 'components/TruncatedText/TruncatedText';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';
import jobseekersService from 'features/jobseekers/jobseekersService';

import useErrorCatch from 'hooks/useErrorCatch';
import useSidebarOpen from 'hooks/useSidebarOpen';

import { JobseekerMatchesData } from 'types/jobseekers';

import {
  ClientTierFeatures,
  ClientTiers,
  Dictionaries,
  JobseekerAttachmentTypes,
  SidebarContentTypes
} from 'constants/enums';
import { STATUS_UPDATED_BY_JOBLIO } from 'constants/notifications';
import { FALLBACK_VALUE } from 'constants/uiSettings';

import MatchesActions from './MatchesActions/MatchesActions';
import styles from './MatchesCard.module.scss';

interface MatchesCardProps {
  jobseekerMatchesData: JobseekerMatchesData;
  jobseekerId: string;
  jobId: string;
  handleSetInviteSent: (jobseekerId: string) => void;
  isAIMatches?: boolean;
}

const MatchesCard = ({
  jobseekerMatchesData,
  jobseekerId,
  jobId,
  handleSetInviteSent,
  isAIMatches
}: MatchesCardProps) => {
  const { t } = useTranslation();
  const [isActionDisabled, setIsActionDisabled] = useState(false);
  const openSidebar = useSidebarOpen();
  const catchError = useErrorCatch();
  const countriesDictionary = useSelector(
    selectDictionary(Dictionaries.ClientCountry)
  );

  const sendJobInviteToJobseeker = async (
    jobseekerId: string,
    jobId: string
  ): Promise<void> => {
    setIsActionDisabled(true);

    try {
      await jobseekersService.sendJobInviteToJobseeker(jobseekerId, jobId);
      handleSetInviteSent(jobseekerId);
    } catch (error) {
      catchError({
        error,
        default: {
          message: 'Operation Failed',
          description: 'Failed to send Job Invite to Jobseeker.'
        }
      });
    } finally {
      setIsActionDisabled(false);
    }
  };

  const handleSendInvite = (jobseekerId: string, jobId: string) => () => {
    sendJobInviteToJobseeker(jobseekerId, jobId);
  };

  return (
    <div className={styles.card}>
      <div className={styles.topBlock}>
        <div className={styles.verifiedAndSkillWrapper}>
          {isAIMatches ? (
            <ProfileMatch matchScore={jobseekerMatchesData.score} />
          ) : (
            <SkillMatch
              skillsetMatchLabel={jobseekerMatchesData.skillsetMatchLabel}
              skillsetsAll={jobseekerMatchesData.skillsetsAll}
              skillsetsMatches={jobseekerMatchesData.skillsetsMatches}
            />
          )}
        </div>
        <div>
          <div className="table-card-title">
            {jobseekerMatchesData.jobseeker?.fullName &&
            jobseekerMatchesData.jobseekerId ? (
              <a
                onClick={openSidebar(
                  SidebarContentTypes.JobseekerView,
                  jobseekerMatchesData.jobseekerId
                )}
              >
                {jobseekerMatchesData.jobseeker.fullName}
              </a>
            ) : (
              FALLBACK_VALUE
            )}
          </div>
          <div className="table-card-label">{t('matches.fullName')}</div>
        </div>
      </div>

      <div>
        {jobseekerMatchesData.jobseeker?.location ? (
          <LocationDisplay
            location={jobseekerMatchesData.jobseeker.location}
            countryCode={getCountryCodeFromApplication(
              jobseekerMatchesData.jobseeker.location.country,
              countriesDictionary
            )}
          />
        ) : (
          FALLBACK_VALUE
        )}
        <div className={styles.label}>{t('matches.jobLocation')}</div>
      </div>

      <div>
        <div className="table-card-title">
          <EyeAttachmentPreview
            attachments={jobseekerMatchesData?.jobseeker?.attachments || []}
            attachmentType={JobseekerAttachmentTypes.CV}
            minimalTier={ClientTiers.Paid}
            feature={ClientTierFeatures.Resumes}
          />
        </div>
        <div className="table-card-label">{t('matches.resume')}</div>
      </div>

      <AvailabilityStatus
        status={jobseekerMatchesData?.jobseeker?.availabilityStatus}
        tooltipTitle={t(STATUS_UPDATED_BY_JOBLIO)}
      />

      {isAIMatches && jobseekerMatchesData.generatedDescription && (
        <TruncatedText
          text={jobseekerMatchesData.generatedDescription}
          className={styles.llmExplane}
        />
      )}

      <MatchesActions
        applied={jobseekerMatchesData.applied}
        inviteSent={jobseekerMatchesData.inviteSent}
        sendInvite={handleSendInvite(jobseekerId, jobId)}
        isActionDisabled={isActionDisabled}
      />
    </div>
  );
};

export default MatchesCard;
